import { useTranslations } from '@vocab/react';
import { useToast } from 'braid-design-system';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CAREER_HISTORY } from 'src/components/Router';
import type { UpdateRouteParams } from 'src/components/Router/reducer';
import { EDIT } from 'src/consts';
import { yearsOfExperienceUpdate } from 'src/modules/analytics/yearsOfExperience/actionCreators';
import { useMutation, useQuery } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import { useFormDirtyStateUpdates } from 'src/modules/detect-unsaved-changes/useFormDirtyStateUpdates';
import { YEARS_OF_EXPERIENCE_FORM } from 'src/reducers/forms';
import {
  GetYearsOfExperienceDocument,
  UpdateYearsOfExperienceDocument,
  type UpdateYearsOfExperienceMutation,
} from 'src/types/graphql';

import translations from './.vocab';
import {
  YearsOfExperienceForm,
  type YearsOfExperience,
} from './YearsOfExperienceForm';

interface EditYearsOfExperienceProps {
  displayDialog: boolean;
  closeYearsOfExperienceDialog: () => void;
  setDisplayYearsOfExperienceDialog: (open: boolean) => void;
  updateRouteFn: (params: UpdateRouteParams) => void;
}

export const EditYearsOfExperience = ({
  displayDialog,
  closeYearsOfExperienceDialog,
  setDisplayYearsOfExperienceDialog,
  updateRouteFn,
}: EditYearsOfExperienceProps) => {
  const { t } = useTranslations(translations);
  const { language } = useAppConfig();
  const dispatch = useDispatch();

  const showToast = useToast();
  const { data, loading, error } = useQuery(GetYearsOfExperienceDocument, {
    variables: { languageCode: language },
  });

  const yearsOfExperience =
    loading || error ? null : data?.viewer?.yearsOfExperience;

  const [initialYearsOfExperience] = useState(yearsOfExperience);

  const updateFormDirtyState = useFormDirtyStateUpdates({
    form: YEARS_OF_EXPERIENCE_FORM,
  });

  const onFormDirtyChange = useCallback(
    (dirty: boolean) => {
      updateFormDirtyState({ dirty });
    },
    [updateFormDirtyState],
  );

  const mutationSuccess = useCallback(
    (mutationResponse: UpdateYearsOfExperienceMutation) => {
      setDisplayYearsOfExperienceDialog(false);

      showToast({
        message: t('Years of experience updated'),
        tone: 'positive',
        key: 'yearsOfExperience',
      });

      const mutationData =
        mutationResponse.updateYearsOfExperience.viewer?.yearsOfExperience;

      if (
        !Boolean(initialYearsOfExperience?.option?.id) &&
        Boolean(mutationData?.option?.id) &&
        mutationData?.newToWorkforce === false
      ) {
        updateRouteFn({
          to: CAREER_HISTORY,
          mode: EDIT,
          analytics: { currentPageSectionState: 'add' },
        });
      }
    },
    [
      initialYearsOfExperience?.option?.id,
      setDisplayYearsOfExperienceDialog,
      showToast,
      t,
      updateRouteFn,
    ],
  );

  const [
    updateYearsOfExperienceInput,
    { loading: mutationLoading, error: mutationError },
  ] = useMutation(UpdateYearsOfExperienceDocument, {
    onCompleted: mutationSuccess,
  });

  const submitForm = useCallback(
    (params: { dirty: boolean; formValues: YearsOfExperience }): void => {
      const { dirty, formValues } = params;

      if (dirty && !mutationLoading) {
        updateYearsOfExperienceInput({
          variables: {
            id: formValues?.option?.id || null,
            newToWorkforce: formValues.newToWorkforce,
            languageCode: language,
          },
        });

        dispatch(
          yearsOfExperienceUpdate({
            actionName: !Boolean(yearsOfExperience?.option?.id)
              ? 'ADD'
              : 'EDIT',
            userIsNewToWorkforce: formValues.newToWorkforce,
          }),
        );
      } else {
        closeYearsOfExperienceDialog();
      }
    },
    [
      closeYearsOfExperienceDialog,
      dispatch,
      language,
      mutationLoading,
      updateYearsOfExperienceInput,
      yearsOfExperience?.option?.id,
    ],
  );

  const onCancel = () => {
    closeYearsOfExperienceDialog();
  };

  return (
    <YearsOfExperienceForm
      initialValues={yearsOfExperience || undefined}
      onFormDirtyChange={onFormDirtyChange}
      submitForm={submitForm}
      onCancel={onCancel}
      displayDialog={displayDialog}
      closeYearsOfExperienceDialog={closeYearsOfExperienceDialog}
      isLoading={mutationLoading}
      hasError={Boolean(mutationError)}
      hasYearsOfExperience={Boolean(yearsOfExperience?.option?.id)}
    />
  );
};
